import { Alpine } from '../../core/alpine-core.js';
import { preferredAppOrigin } from '../../core/preferred-app-origin.js';
import ApexCharts from 'apexcharts';
import QRCode from 'qrcode';
import { apiFetch } from '../api.js';
import { unwrapApiEntity } from '../../core/api-entity.js';
import { CURRENT_SEASON } from '../../core/current-season.js';
import { adminLeagueDataSurface, effectiveAdminLeagueId, withLeagueId } from '../league-context.js';

import { buildTeamPoints, buildPlayerBestTon, buildTeamsBigsStacked } from './charts-builders.js';
import { playerAvatarUrl as resolvePlayerAvatarUrl } from '../player-avatar-url.js';

/** Base key; actual storage key is suffixed per signed-in user so layouts are not shared across accounts. */
const ADMIN_STATS_STRIP_LS_KEY = 'darts.adminStatsStrip.layoutKey';
const ADMIN_DASHBOARD_TAB_LS_KEY = 'darts.adminDashboard.activeTab';

function fmtDateTime(dtStr) {
    const d = new Date(dtStr);
    const short = d.toLocaleString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    const long = d.toLocaleString('en-GB');
    return { short, long, raw: d };
}

function timeUntil(d) {
    const ms = +d - Date.now();
    if (ms <= 0) return 'now';
    const minutes = Math.floor(ms / 60000);
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hr`;
    const days = Math.floor(hours / 24);
    return `${days} day${days > 1 ? 's' : ''}`;
}

function featureToPillLabel(feature) {
    const raw = String(feature || '').trim();
    if (!raw) return '';
    const pretty = raw
        .replaceAll(/[_-]+/g, ' ')
        .replaceAll(/\s+/g, ' ')
        .trim();
    return pretty
        .split(' ')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}

Alpine.data('statsDashboard', () => ({
    dark: Alpine.store('theme')?.value === 'dark',
    updatedAtLabel: '',
    season: CURRENT_SEASON,
    availableSeasons: [CURRENT_SEASON],
    seasonLoading: false,
    teams: [],
    playersTop: [],
    me: null,
    summary: null,

    nextGame: null,
    nextLoading: true,
    nextError: '',
    lastGame: null,
    lastLoading: true,
    lastError: '',

    charts: { team: null, playersBest: null, teamsBigs: null },
    _observers: [],
    _leagueSnap: '',
    _onLeagueOrNav: null,

    statsStripLayoutKey: 'default',
    /** `account` = layoutKey default/combined; `league` = saved strip for one competition. */
    statsStripLayoutScope: 'account',
    statsStripFocusLeagueKey: null,
    statsStripWidgets: [],
    statsStripLoading: false,
    statsStripError: '',
    statsStripSeasonUsed: '',
    statsStripMetaLeagues: [],

    /** Account control panel (subscription + cross-league fixtures). */
    effectiveCapabilities: null,
    accountFixturesLeagues: [],
    accountFixturesLoading: false,
    backupSyncStatus: null,
    playZoneLoading: false,
    recentChallenges: [],
    trainingMine: [],
    trainingSummary: null,
    trainingProgress: null,
    recentTrainingBadges: [],
    adminHomeSections: [],
    /** Full `admin_home` widgets from layout API (for control-panel custom shortcut). */
    adminHomeLayoutWidgets: [],
    accountPanelError: '',
    dashboardActiveTab: 'account',
    dashboardLeagueTabs: [],

    /** Snapshot from `/api/admin/account-settings` for dashboard subscription card (same payload as account settings). */
    accountControlPlane: null,
    accountSettingsBrief: null,
    /** `onboarding`, `onboardingHints`, `onboardingStepLocked` from account-settings GET. */
    onboardingDash: null,

    venueKioskQrDataUrl: '',
    venueKioskQrError: '',
    demoImportBusy: false,
    demoImportModalOpen: false,
    /** `confirm` = warning + actions; `progress` = import running */
    demoImportPhase: 'confirm',
    demoImportError: '',
    demoImportClearExisting: false,
    demoImportModules: {
        venues: true,
        leagueRules: true,
        teams: true,
        players: true,
        fixtures: true,
    },

    /** Compact snapshot for "Build and run your darts platform" cards (admin). */
    platformSetupMetaLeagues: [],
    platformSetupFixtureCount: 0,
    platformSetupTeamsTotal: 0,
    platformSetupTeamNames: [],
    platformSetupVenuesTotal: 0,
    platformSetupVenueNames: [],
    platformSetupRulesCount: 0,
    platformSetupRuleSummaries: [],
    platformSetupPlayersTotal: 0,
    platformSetupPlayerNames: [],

    teamStat: 'pts',
    playerStat: 'tons',
    teamStatLabel(key) {
        return {
            pts: 'Pts',
            matches: 'P',
            wins: 'W',
            draws: 'D',
            losses: 'L',
            legsDiff: 'Leg +/-'
        }[key] || key;
    },
    teamStatValue(t, key) {
        switch (key) {
            case 'pts': return t.points ?? 0;
            case 'matches': return t.matches ?? 0;
            case 'wins': return t.wins ?? 0;
            case 'draws': return t.draws ?? 0;
            case 'losses': return t.losses ?? 0;
            case 'legsDiff': return (t.legsWon ?? 0) - (t.legsLost ?? 0);
            default: return 0;
        }
    },
    playerStatLabel(key) {
        return {
            legs: 'Legs',
            tons: 'Tons',
            closes: 'Closes',
            highCheckout: 'High CO',
            singles: 'Singles',
            apps: 'Apps'
        }[key] || key;
    },
    playerStatValue(p, key) {
        switch (key) {
            case 'legs': return p.legsWon ?? 0;
            case 'tons': return p.tons ?? 0;
            case 'closes': return p.closes ?? 0;
            case 'highCheckout': return p.highestCheckout ?? 0;
            case 'singles': return p.singlesWon ?? 0;
            case 'apps': return p.appearances ?? 0;
            default: return 0;
        }
    },
    teamMobileSort: 'points',
    teamMobileExpandedId: '',

    teamLegDiff(team) {
        return (Number(team?.legsWon || 0) - Number(team?.legsLost || 0));
    },

    teamMobileSortValue(team) {
        switch (this.teamMobileSort) {
            case 'form': return Number(team?.wins || 0) - Number(team?.losses || 0);
            case 'legsDiff': return this.teamLegDiff(team);
            case 'wins': return Number(team?.wins || 0);
            case 'points':
            default:
                return Number(team?.points || 0);
        }
    },

    teamFormLine(team) {
        return `${Number(team?.wins || 0)}-${Number(team?.draws || 0)}-${Number(team?.losses || 0)}`;
    },

    teamMobileRows() {
        const rows = Array.isArray(this.teams) ? this.teams.slice() : [];
        const sorted = rows.toSorted((a, b) => {
            const primary = this.teamMobileSortValue(b) - this.teamMobileSortValue(a);
            if (primary) return primary;
            const points = Number(b?.points || 0) - Number(a?.points || 0);
            if (points) return points;
            const diff = this.teamLegDiff(b) - this.teamLegDiff(a);
            if (diff) return diff;
            return String(a?.teamName || '').localeCompare(String(b?.teamName || ''));
        });
        return sorted.map((team, index) => ({ ...team, rank: index + 1 }));
    },

    rankTone(rank) {
        if (rank === 1) return 'bg-amber-500/20 text-amber-700 ring-1 ring-amber-400/35 dark:text-amber-200';
        if (rank === 2) return 'bg-slate-300/40 text-slate-700 ring-1 ring-slate-400/35 dark:bg-slate-500/20 dark:text-slate-200';
        if (rank === 3) return 'bg-orange-500/20 text-orange-700 ring-1 ring-orange-400/35 dark:text-orange-200';
        return 'bg-slate-200/60 text-slate-700 ring-1 ring-slate-300/60 dark:bg-neutral-800 dark:text-neutral-200 dark:ring-neutral-700';
    },

    teamMobileMetricLabel() {
        if (this.teamMobileSort === 'form') return 'Form';
        if (this.teamMobileSort === 'legsDiff') return 'Leg +/-';
        if (this.teamMobileSort === 'wins') return 'Wins';
        return 'Pts';
    },

    teamMobileMetricValue(team) {
        if (this.teamMobileSort === 'form') return this.teamFormLine(team);
        if (this.teamMobileSort === 'legsDiff') {
            const diff = this.teamLegDiff(team);
            return diff > 0 ? `+${diff}` : `${diff}`;
        }
        return String(this.teamMobileSortValue(team));
    },

    toggleTeamMobileExpanded(team) {
        const id = String(team?.teamId || team?._id || '');
        this.teamMobileExpandedId = this.teamMobileExpandedId === id ? '' : id;
    },

    isTeamMobileExpanded(team) {
        const id = String(team?.teamId || team?._id || '');
        return !!id && this.teamMobileExpandedId === id;
    },

    isAdminUser() {
        try {
            return !!Alpine.store('session')?.hasRole?.('admin');
        } catch {
            return false;
        }
    },

    playerAvatarUrl(playerLike) {
        return resolvePlayerAvatarUrl(playerLike);
    },

    /** Owner or venue admin only — matches `requireAdminRole("admin")` on the server (managers excluded). */
    isStrictVenueAdminOrOwner() {
        try {
            const r = String(Alpine.store('session')?.me?.role || '').toLowerCase();
            return r === 'owner' || r === 'admin';
        } catch {
            return false;
        }
    },

    /** Banner on dashboard when tenant onboarding checklist is not finished or dismissed. */
    onboardingBannerVisible() {
        if (!this.isAdminUser()) return false;
        const o = this.onboardingDash?.onboarding;
        if (!o || o.completedAt || o.dismissedAt) return false;
        const s = o.steps && typeof o.steps === 'object' ? o.steps : {};
        const hints = this.onboardingDash?.hints || {};
        const needSub = hints.subscriptionRequired !== false;
        const keys = ['accountId', 'subscription', 'demoImport', 'inviteUsers'];
        return keys.some((id) => {
            if (id === 'subscription' && !needSub) return false;
            return !s[id];
        });
    },

    cpLeagueAllowed() {
        return !!this.effectiveCapabilities?.league;
    },

    cpKioskAllowed() {
        return !!this.effectiveCapabilities?.kiosk;
    },

    cpChallengesAllowed() {
        return !!this.effectiveCapabilities?.challenges;
    },

    cpTrainingAllowed() {
        return !!this.effectiveCapabilities?.training;
    },

    addPlayerHref() {
        return '/admin/players/new';
    },

    addTeamHref() {
        return '/admin/teams/new';
    },

    cpLiveFixturesAllowed() {
        return !!this.effectiveCapabilities?.liveFixtures;
    },

    cpBackupSyncAllowed() {
        return !!this.effectiveCapabilities?.backupSync;
    },

    cpPlayerPortalAllowed() {
        return !!this.effectiveCapabilities?.playerPortal;
    },

    playerPortalHref() {
        return '/login.html';
    },

    /** True when any subscription shortcut below Play zone should show. */
    dashboardPlanShortcutsVisible() {
        return !!(
            this.cpKioskAllowed()
            || this.cpLeagueAllowed()
            || this.cpLiveFixturesAllowed()
            || this.cpBackupSyncAllowed()
            || this.cpPlayerPortalAllowed()
        );
    },

    subscriptionCardCp() {
        return this.accountControlPlane;
    },

    subscriptionCardEntitlement() {
        return this.subscriptionCardCp()?.entitlement || null;
    },

    subscriptionCardBackupSyncEntitled() {
        const cp = this.subscriptionCardCp();
        if (!cp?.configured) return true;
        const e = cp.entitlement;
        if (!e || cp.error) return false;
        if (e.noSubscription === true) return false;
        if (e.backupSyncAllowed === true) return true;
        const feats = Array.isArray(e.features) ? e.features : [];
        return feats.map((f) => String(f).toLowerCase()).includes('backup_sync');
    },

    subscriptionCardCurrentPlan() {
        const ent = this.subscriptionCardEntitlement();
        const tier = String(ent?.tier || '').trim();
        const plans = Array.isArray(this.subscriptionCardCp()?.plans) ? this.subscriptionCardCp().plans : [];
        return plans.find((p) => String(p?.id || '') === tier) || null;
    },

    subscriptionCardPlanLabel() {
        const plan = this.subscriptionCardCurrentPlan();
        if (plan?.label) return String(plan.label);
        const tier = String(this.subscriptionCardEntitlement()?.tier || '').trim();
        if (!tier) return '—';
        /** Tier is set but not in the published `plans` list — complimentary, legacy, or control-plane custom bundle. */
        return 'Custom';
    },

    subscriptionCardFeaturePills() {
        const ent = this.subscriptionCardEntitlement();
        const feats = Array.isArray(ent?.features) ? ent.features : [];
        if (feats.length) return feats.map(featureToPillLabel).filter(Boolean);
        try {
            const g = Alpine.store('subscriptionGate');
            const gf = Array.isArray(g?.entitlementFeatures) ? g.entitlementFeatures : [];
            return gf.map(featureToPillLabel).filter(Boolean);
        } catch {
            return [];
        }
    },

    subscriptionCardEntitlementTone() {
        try {
            if (Alpine.store('subscriptionGate')?.blocked) return 'warn';
        } catch {
            /* ignore */
        }
        const ent = this.subscriptionCardEntitlement();
        if (!ent) return 'neutral';
        if (ent.kioskAllowed) return 'ok';
        if (ent.noSubscription || ent.upgradeRequired) return 'warn';
        return 'neutral';
    },

    subscriptionCardEntitlementMessage() {
        try {
            const g = Alpine.store('subscriptionGate');
            if (g?.blocked && String(g?.reason || '').trim()) return String(g.reason).trim();
        } catch {
            /* ignore */
        }
        const cp = this.subscriptionCardCp();
        const ent = this.subscriptionCardEntitlement();
        if (!cp?.configured) {
            return 'Control-plane entitlement checks are not configured on this server yet.';
        }
        if (cp?.error) return String(cp.error);
        if (!ent) return 'Open Account settings to connect billing or enter a subscription key when you are ready.';
        if (ent.kioskAllowed) return `Kiosk feature is active for account ${ent.accountId || ''}.`;
        if (ent.noSubscription) {
            return 'No active subscription on this account yet. Use Account settings to add a subscription key or connect billing when you are ready — league and venue tools can still be explored.';
        }
        if (ent.upgradeRequired) return 'Current plan does not include kiosk entitlement.';
        return 'Subscription is present but kiosk entitlement is unavailable.';
    },

    /** Main card title: avoid "Entitlement status" before there is a real subscription / plan to summarise. */
    subscriptionDashboardCardTitle() {
        const ent = this.subscriptionCardEntitlement();
        if (!ent) return 'Subscription';
        if (ent.upgradeRequired) return 'Plan upgrade needed';
        if (ent.noSubscription === true) return 'Subscription';
        return 'Entitlement status';
    },

    /** Hide plan / privilege / backup pills until there is a non–no-subscription entitlement to summarise. */
    subscriptionCardShowPlanDetails() {
        const ent = this.subscriptionCardEntitlement();
        if (!ent) return false;
        if (ent.noSubscription === true) return false;
        return true;
    },

    /** Inline activation card over the control-panel grid (Account tab); mirrors global gate state. */
    subscriptionControlPanelActivationOverlayVisible() {
        if (!this.isAdminUser() || !this.isAccountDashboardTab()) return false;
        try {
            const g = Alpine.store('subscriptionGate');
            return !!(g?.loaded && g.blocked);
        } catch {
            return false;
        }
    },

    subscriptionCardOuterClass() {
        const t = this.subscriptionCardEntitlementTone();
        if (t === 'ok') return 'border-emerald-500/35 bg-emerald-500/10';
        if (t === 'warn') return 'border-amber-500/35 bg-amber-500/10';
        return 'border-white/10 bg-black/25';
    },

    subscriptionCardStoredKeyLine() {
        const s = this.accountSettingsBrief;
        if (!s?.hasSubscriptionKey) return '';
        const mask = String(s.subscriptionKeyMask || '').trim();
        return `Stored key: ${mask || '…'}`;
    },

    subscriptionCardActivatedLine() {
        const s = this.accountSettingsBrief;
        if (!s?.subscriptionActivatedAt) return '';
        try {
            return `Activated: ${new Date(s.subscriptionActivatedAt).toLocaleString()}`;
        } catch {
            return '';
        }
    },

    addVenueHref() {
        return '/admin/venues/new';
    },

    leagueBuilderHref() {
        return '/admin/league-builder';
    },

    leagueRulesHref() {
        return '/admin/league-rules/new';
    },

    challengesHref() {
        return '/admin/challenges';
    },

    trainingHref() {
        return '/admin/training';
    },

    fixturesHref(leagueKey = '') {
        return withLeagueId('/admin/fixtures', leagueKey || effectiveAdminLeagueId());
    },

    leagueTableHref(leagueKey = '') {
        return withLeagueId('/admin/league-table', leagueKey || effectiveAdminLeagueId());
    },

    activeLeagueDashboardKey() {
        if (this.isLeagueDashboardTab()) {
            return String(this.dashboardActiveTab.slice('league:'.length) || '').trim().toUpperCase();
        }
        return String(effectiveAdminLeagueId() || '').trim().toUpperCase();
    },

    leagueSettingsHref(leagueKey = '') {
        return this.leagueDetailNavHref(leagueKey || this.activeLeagueDashboardKey());
    },

    rememberLeagueForSettingsNav(leagueKey = '') {
        const lk = String(leagueKey || this.activeLeagueDashboardKey() || '').trim().toUpperCase();
        this.persistActiveLeagueId(lk);
    },

    leagueDetailNavHref(leagueKey = '') {
        const lk = String(
            leagueKey || this.activeLeagueDashboardKey() || effectiveAdminLeagueId(),
        ).trim().toUpperCase();
        return /^[A-Z0-9_-]{1,24}$/.test(lk) ? `/admin/l/${encodeURIComponent(lk)}` : '/admin/dashboard';
    },

    challengeStatusLabel(status) {
        const key = String(status || '').trim().toLowerCase();
        if (!key) return 'Unknown';
        return key.replaceAll('_', ' ').replaceAll(/\b\w/g, (ch) => ch.toUpperCase());
    },

    challengeOpponentName(item) {
        const meId = String(Alpine.store('session')?.me?._id || '');
        const challenger = item?.challengerPlayerId || {};
        const challenged = item?.challengedPlayerId || {};
        const challengerId = String(challenger?._id || item?.challengerPlayerId || '');
        const pick = challengerId === meId ? challenged : challenger;
        return String(pick?.nickname || pick?.fullName || 'Opponent');
    },

    challengeRowTitle(item) {
        if (!item) return 'Challenge';
        const hasTitle = String(item?.title || '').trim();
        if (hasTitle) return hasTitle;
        return `vs ${this.challengeOpponentName(item)}`;
    },

    challengeUpdatedLabel(item) {
        const dt = item?.updatedAt || item?.createdAt;
        return dt ? this.formatFriendlyDateTime(dt) : '';
    },

    trainingLevelsDone() {
        const levels = new Set();
        const rows = Array.isArray(this.trainingProgress?.templateProgress)
            ? this.trainingProgress.templateProgress
            : [];
        for (const row of rows) {
            if (String(row?.bestAwardTier || 'none') === 'none') continue;
            const level = String(row?.levelId || '').trim();
            if (level) levels.add(level.toUpperCase());
        }
        return [...levels];
    },

    trainingLevelsDoneLabel() {
        const levels = this.trainingLevelsDone();
        if (!levels.length) return 'No levels recorded yet';
        return levels.join(', ');
    },

    trainingCurrentLevel() {
        const done = Number(this.trainingSummary?.completedTemplates || 0);
        return done > 0 ? done : 0;
    },

    summarizeTrainingSessions(items) {
        const list = Array.isArray(items) ? items : [];
        const completed = list.filter((s) => String(s?.status || '') === 'completed').length;
        const inProgress = list.filter((s) => String(s?.status || '') === 'in_progress').length;
        return {
            total: list.length,
            completed,
            inProgress,
        };
    },

    badgeTierClass(tier) {
        const k = String(tier || '').toLowerCase();
        if (k === 'gold') return 'border-amber-300/40 bg-amber-500/20 text-amber-100';
        if (k === 'silver') return 'border-slate-300/40 bg-slate-400/20 text-slate-100';
        if (k === 'bronze') return 'border-orange-300/40 bg-orange-500/20 text-orange-100';
        return 'border-white/10 bg-white/5 text-neutral-300';
    },

    backupScheduleLabel() {
        const v = String(this.backupSyncStatus?.schedulePreset || '').trim();
        if (v === 'weekly_sunday') return 'Each week (Sunday)';
        if (v === 'month_end') return 'End of month';
        return 'Every day';
    },

    formatFriendlyDateTime(iso) {
        const s = String(iso || '').trim();
        if (!s) return '';
        const d = new Date(s);
        if (Number.isNaN(d.getTime())) return s;
        return new Intl.DateTimeFormat(undefined, {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            timeZoneName: 'short',
        }).format(d);
    },

    normalizeAdminHomeSections(widgets = []) {
        const defaults = [
            { key: 'control_panel', enabled: true, order: 0 },
            { key: 'challenges_training', enabled: true, order: 1 },
            { key: 'plan_shortcuts', enabled: true, order: 2 },
            { key: 'subscription', enabled: true, order: 3 },
            { key: 'backup_restore', enabled: true, order: 4 },
            { key: 'upcoming_by_league', enabled: true, order: 5 },
            { key: 'admin_stats', enabled: true, order: 6 },
        ];
        const found = new Map();
        for (const w of Array.isArray(widgets) ? widgets : []) {
            const key = String(w?.config?.sectionKey || '').trim();
            if (!defaults.some((d) => d.key === key)) continue;
            found.set(key, {
                key,
                enabled: w?.config?.enabled !== false,
                order: Number.isFinite(Number(w?.order)) ? Number(w.order) : defaults.find((d) => d.key === key)?.order ?? 0,
            });
        }
        const sectionTie = (k) =>
            ['control_panel', 'challenges_training', 'plan_shortcuts', 'subscription', 'backup_restore', 'upcoming_by_league', 'admin_stats'].indexOf(k);
        this.adminHomeSections = defaults
            .map((d) => found.get(d.key) || d)
            .sort((a, b) => {
                const ao = Number(a.order) || 0;
                const bo = Number(b.order) || 0;
                if (ao !== bo) return ao - bo;
                return sectionTie(a.key) - sectionTie(b.key);
            });
    },

    sectionEnabled(sectionKey) {
        const key = String(sectionKey || '').trim();
        const row = this.adminHomeSections.find((s) => s.key === key);
        if (row) return row.enabled !== false;
        return true;
    },

    orderedAdminSections() {
        return this.adminHomeSections
            .filter((s) => s.enabled !== false)
            .toSorted((a, b) => a.order - b.order);
    },

    fixtureRowLabel(fx) {
        const when = fx?.startAt ? new Date(fx.startAt).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }) : '';
        const rd = String(fx?.roundLabel || '').trim();
        const h = String(fx?.homeTeamName || '').trim() || 'Home';
        const a = String(fx?.awayTeamName || '').trim() || 'Away';
        return [rd || 'Fixture', `${h} vs ${a}`, when].filter(Boolean).join(' · ');
    },

    openVenueKiosk() {
        if (!this.cpKioskAllowed()) return;
        try {
            const base = globalThis.location?.origin || '';
            globalThis.open(`${base}/venue-kiosk`, '_blank', 'noopener,noreferrer');
        } catch {
            /* ignore */
        }
    },

    resolveVenueKioskLaunchUrl() {
        const origin = String(preferredAppOrigin() || '').trim();
        if (!origin) return '/venue-kiosk';
        return `${origin}/venue-kiosk`;
    },

    controlPanelLayoutWidget() {
        const widgets = Array.isArray(this.adminHomeLayoutWidgets) ? this.adminHomeLayoutWidgets : [];
        return widgets.find((x) => String(x?.config?.sectionKey || '').trim() === 'control_panel') || null;
    },

    controlPanelShortcutTarget() {
        return String(this.controlPanelLayoutWidget()?.config?.shortcutTarget || '').trim();
    },

    controlPanelShortcutConfigured() {
        return !!this.controlPanelShortcutTarget();
    },

    controlPanelShortcutDefaultIconForTarget(target) {
        const t = String(target || '').trim();
        const map = {
            venue_kiosk: 'fa-tablet-screen-button',
            league_builder: 'fa-wand-magic-sparkles',
            venues: 'fa-location-dot',
            league_rules: 'fa-list-check',
            teams: 'fa-people-group',
            players: 'fa-user-plus',
            fixtures: 'fa-calendar-days',
            league_table: 'fa-ranking-star',
            challenges: 'fa-trophy',
            training: 'fa-bullseye',
            account_layout: 'fa-table-columns',
        };
        return map[t] || 'fa-link';
    },

    controlPanelShortcutLabelForTarget(target) {
        const t = String(target || '').trim();
        const map = {
            venue_kiosk: 'Venue kiosk',
            league_builder: 'League & fixtures',
            venues: 'Venues',
            league_rules: 'League rules',
            teams: 'Teams',
            players: 'Players',
            fixtures: 'Fixtures',
            league_table: 'League table',
            challenges: 'Challenges',
            training: 'Training',
            account_layout: 'Dashboard layout',
        };
        return map[t] || 'Shortcut';
    },

    controlPanelShortcutIconClass() {
        const cfg = this.controlPanelLayoutWidget()?.config || {};
        let icon = String(cfg.shortcutIcon || '').trim();
        if (!icon) icon = this.controlPanelShortcutDefaultIconForTarget(this.controlPanelShortcutTarget());
        if (!/^fa-[a-z0-9-]+$/i.test(icon)) icon = 'fa-link';
        return `fa-solid ${icon}`;
    },

    controlPanelShortcutDisplayTitle() {
        const custom = String(this.controlPanelLayoutWidget()?.config?.shortcutTitle || '').trim();
        if (custom) return custom;
        return this.controlPanelShortcutLabelForTarget(this.controlPanelShortcutTarget());
    },

    controlPanelShortcutSubtitle() {
        const t = this.controlPanelShortcutTarget();
        if (t === 'venue_kiosk') return 'Opens in a new tab · scan QR on a tablet';
        if (t === 'account_layout') return 'Account settings → Layout';
        return '';
    },

    controlPanelShortcutHref() {
        const t = this.controlPanelShortcutTarget();
        if (!t) return '';
        switch (t) {
            case 'venue_kiosk':
                return this.resolveVenueKioskLaunchUrl();
            case 'league_builder':
                return this.leagueBuilderHref();
            case 'venues':
                return this.addVenueHref();
            case 'league_rules':
                return this.leagueRulesHref();
            case 'teams':
                return this.addTeamHref();
            case 'players':
                return this.addPlayerHref();
            case 'fixtures':
                return this.fixturesHref();
            case 'league_table':
                return this.leagueTableHref();
            case 'challenges':
                return this.challengesHref();
            case 'training':
                return this.trainingHref();
            case 'account_layout':
                return '/admin/account-settings#layout';
            default:
                return '';
        }
    },

    controlPanelShortcutOpenBlank() {
        return this.controlPanelShortcutTarget() === 'venue_kiosk';
    },

    controlPanelShortcutAsLink() {
        if (!this.controlPanelShortcutConfigured()) return false;
        const href = String(this.controlPanelShortcutHref() || '').trim();
        if (!href) return false;
        if (this.controlPanelShortcutTarget() === 'venue_kiosk' && !this.cpKioskAllowed()) return false;
        return true;
    },

    openDemoImportModal() {
        if (!this.isStrictVenueAdminOrOwner() || this.demoImportBusy) return;
        this.demoImportError = '';
        this.demoImportPhase = 'confirm';
        this.demoImportClearExisting = false;
        this.demoImportModules = {
            venues: true,
            leagueRules: true,
            teams: true,
            players: true,
            fixtures: true,
        };
        this.demoImportModalOpen = true;
    },

    closeDemoImportModal() {
        if (this.demoImportPhase === 'progress') return;
        this.demoImportModalOpen = false;
        this.demoImportPhase = 'confirm';
        this.demoImportError = '';
    },

    toggleDemoImportModule(key) {
        if (!this.demoImportModules || !(key in this.demoImportModules)) return;
        this.demoImportModules[key] = !this.demoImportModules[key];
    },

    async confirmDemoImport() {
        if (!this.isStrictVenueAdminOrOwner() || this.demoImportBusy) return;
        const m = this.demoImportModules || {};
        if (!m.venues && !m.leagueRules && !m.teams && !m.players && !m.fixtures) {
            this.demoImportError = 'Select at least one category to import.';
            return;
        }
        this.demoImportError = '';
        this.demoImportPhase = 'progress';
        this.demoImportBusy = true;
        try {
            const res = await apiFetch('/admin/account-settings/demo-import', {
                method: 'POST',
                body: {
                    clearExisting: !!this.demoImportClearExisting,
                    importVenues: !!m.venues,
                    importLeagueRules: !!m.leagueRules,
                    importTeams: !!m.teams,
                    importPlayers: !!m.players,
                    importFixtures: !!m.fixtures,
                },
                timeoutMs: 10 * 60 * 1000,
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data?.error || data?.message || 'Demo import failed');
            this.demoImportModalOpen = false;
            this.demoImportPhase = 'confirm';
            this.$store?.toastr?.success?.('Demo import complete. Reloading...', 2200);
            globalThis.setTimeout(() => globalThis.location.reload(), 900);
        } catch (e) {
            this.demoImportPhase = 'confirm';
            this.demoImportError = e?.message || 'Demo import failed';
            this.$store?.toastr?.error?.(this.demoImportError, 4000);
        } finally {
            this.demoImportBusy = false;
        }
    },

    async refreshVenueKioskQr() {
        this.venueKioskQrError = '';
        if (!this.cpKioskAllowed()) {
            this.venueKioskQrDataUrl = '';
            return;
        }
        const url = this.resolveVenueKioskLaunchUrl();
        try {
            this.venueKioskQrDataUrl = await QRCode.toDataURL(url, {
                width: 160,
                margin: 1,
                color: { dark: '#0f172a', light: '#ffffff' },
            });
        } catch {
            this.venueKioskQrDataUrl = '';
            this.venueKioskQrError = 'Could not generate QR code';
        }
    },

    _resetPlatformSetupSnapshot() {
        this.platformSetupMetaLeagues = [];
        this.platformSetupFixtureCount = 0;
        this.platformSetupTeamsTotal = 0;
        this.platformSetupTeamNames = [];
        this.platformSetupVenuesTotal = 0;
        this.platformSetupVenueNames = [];
        this.platformSetupRulesCount = 0;
        this.platformSetupRuleSummaries = [];
        this.platformSetupPlayersTotal = 0;
        this.platformSetupPlayerNames = [];
    },

    cpSetupLeagueScheduleDone() {
        return Number(this.platformSetupFixtureCount || 0) > 0;
    },

    cpSetupVenuesDone() {
        return Number(this.platformSetupVenuesTotal || 0) > 0;
    },

    cpSetupRulesDone() {
        return Number(this.platformSetupRulesCount || 0) > 0;
    },

    cpSetupTeamsDone() {
        return Number(this.platformSetupTeamsTotal || 0) > 0;
    },

    cpSetupPlayersDone() {
        return Number(this.platformSetupPlayersTotal || 0) > 0;
    },

    platformSetupLeagueSubtitle() {
        const n = Number(this.platformSetupFixtureCount || 0);
        const keys = Array.isArray(this.platformSetupMetaLeagues) ? this.platformSetupMetaLeagues : [];
        const keyLine = keys.length
            ? keys.slice(0, 4).map((o) => String(o?.leagueKey || o?.label || '').trim()).filter(Boolean).join(', ')
            : '';
        if (n > 0) {
            const cap = n >= 300 ? '300+' : String(n);
            return [keyLine ? `${keyLine} ·` : null, `${cap} fixtures this season`].filter(Boolean).join(' ');
        }
        if (keyLine) return `Leagues: ${keyLine}`;
        return 'Open League Builder to add rounds and generate fixtures.';
    },

    platformSetupVenuesSubtitle() {
        const t = Number(this.platformSetupVenuesTotal || 0);
        if (!t) return 'Add at least one pub or venue for match nights.';
        const bits = (this.platformSetupVenueNames || []).slice(0, 2).join(' · ');
        if (!bits) return `${t} saved`;
        const more = t > 2 ? ` +${t - 2}` : '';
        return `${t} · ${bits}${more}`;
    },

    platformSetupRulesSubtitle() {
        const c = Number(this.platformSetupRulesCount || 0);
        if (!c) return 'Define checkout rules and match programs per league.';
        const bits = (this.platformSetupRuleSummaries || []).slice(0, 2).join(' · ');
        if (!bits) return `${c} configured`;
        const more = c > 2 ? ` +${c - 2}` : '';
        return `${c} set · ${bits}${more}`;
    },

    platformSetupTeamsSubtitle() {
        const t = Number(this.platformSetupTeamsTotal || 0);
        if (!t) return 'Create teams, then assign players and venues.';
        const bits = (this.platformSetupTeamNames || []).slice(0, 2).join(' · ');
        if (!bits) return `${t} created`;
        const more = t > 2 ? ` +${t - 2}` : '';
        return `${t} · ${bits}${more}`;
    },

    platformSetupPlayersSubtitle() {
        const t = Number(this.platformSetupPlayersTotal || 0);
        if (!t) return 'Build your account player pool for rosters.';
        const bits = (this.platformSetupPlayerNames || []).slice(0, 2).join(' · ');
        if (!bits) return `${t} in pool`;
        const more = t > 2 ? ` +${t - 2}` : '';
        return `${t} · ${bits}${more}`;
    },

    async loadPlatformSetupSnapshot() {
        if (!this.isAdminUser()) {
            this._resetPlatformSetupSnapshot();
            return;
        }
        try {
            const [metaR, teamsR, venuesR, rulesR, playersR] = await Promise.all([
                apiFetch('/dashboard-layout/meta'),
                apiFetch('/teams?page=1&limit=5&sortBy=name:asc&active=true'),
                apiFetch('/venues?page=1&limit=5&sortBy=name:asc'),
                apiFetch('/league-rules?limit=40'),
                apiFetch('/players?page=1&limit=5&sortBy=fullName:asc&includeRemoved=false&populate=false'),
            ]);

            const metaJ = metaR.ok ? await metaR.json().catch(() => ({})) : {};
            this.platformSetupMetaLeagues = Array.isArray(metaJ.leagueOptions) ? metaJ.leagueOptions : [];
            const fxOpt = Array.isArray(metaJ.fixtureOptions) ? metaJ.fixtureOptions : [];
            this.platformSetupFixtureCount = fxOpt.length;

            const teamsJ = teamsR.ok ? await teamsR.json().catch(() => ({})) : {};
            this.platformSetupTeamsTotal = Number(teamsJ.total || 0);
            this.platformSetupTeamNames = (teamsJ.items || [])
                .map((x) => String(x?.name || '').trim())
                .filter(Boolean);

            const venuesJ = venuesR.ok ? await venuesR.json().catch(() => ({})) : {};
            this.platformSetupVenuesTotal = Number(venuesJ.total ?? venuesJ.totalResults ?? 0);
            this.platformSetupVenueNames = (venuesJ.items || [])
                .map((x) => String(x?.name || '').trim())
                .filter(Boolean);

            const rulesJ = rulesR.ok ? await rulesR.json().catch(() => ({})) : {};
            const ruleItems = Array.isArray(rulesJ.items) ? rulesJ.items : [];
            this.platformSetupRulesCount = ruleItems.length;
            this.platformSetupRuleSummaries = ruleItems.slice(0, 6).map((r) => {
                const k = String(r?.leagueKey || '').toUpperCase();
                const s = String(r?.season || '').trim();
                return s ? `${k} ${s}` : k;
            }).filter(Boolean);

            const playersJ = playersR.ok ? await playersR.json().catch(() => ({})) : {};
            this.platformSetupPlayersTotal = Number(playersJ.total || 0);
            this.platformSetupPlayerNames = (playersJ.items || [])
                .map((x) => String(x?.fullName || x?.nickname || '').trim())
                .filter(Boolean);
        } catch {
            this._resetPlatformSetupSnapshot();
        }
    },

    async loadAccountControlPanel() {
        if (!this.isAdminUser()) {
            this.accountFixturesLoading = false;
            this._resetPlatformSetupSnapshot();
            return;
        }
        this.accountFixturesLoading = true;
        this.playZoneLoading = true;
        this.accountPanelError = '';
        try {
            const [aRes, fRes, bRes] = await Promise.all([
                apiFetch('/api/admin/account-settings'),
                apiFetch(
                    `/dashboard-layout/account-fixtures-summary?season=${encodeURIComponent(this.season)}`,
                ),
                apiFetch('/api/admin/backup-sync'),
            ]);
            const aj = await aRes.json().catch(() => ({}));
            const fj = await fRes.json().catch(() => ({}));
            const bj = await bRes.json().catch(() => ({}));
            if (aRes.ok) {
                this.effectiveCapabilities = aj.effectiveCapabilities || null;
                Alpine.store('features')?.applyServerCapabilities?.(aj.effectiveCapabilities);
                Alpine.store('subscriptionGate')?.setFromAccountSettings?.(aj);
                this.accountControlPlane = aj.controlPlane ?? null;
                this.accountSettingsBrief = aj.settings ?? null;
                this.onboardingDash = {
                    onboarding: aj.onboarding || null,
                    hints: aj.onboardingHints || {},
                    stepLocked: aj.onboardingStepLocked || {},
                };
            } else {
                this.effectiveCapabilities = null;
                this.accountControlPlane = null;
                this.accountSettingsBrief = null;
                this.onboardingDash = null;
            }
            if (fRes.ok && Array.isArray(fj.leagues)) {
                this.accountFixturesLeagues = this.enrichAccountFixturesLeagues(fj.leagues);
            } else {
                this.accountFixturesLeagues = [];
                if (!fRes.ok) this.accountPanelError = fj.message || 'Could not load fixture summary';
            }
            if (bRes.ok) {
                this.backupSyncStatus = bj;
            } else {
                this.backupSyncStatus = null;
            }

            this.recentChallenges = [];
            this.trainingMine = [];
            this.trainingSummary = null;
            this.trainingProgress = null;
            this.recentTrainingBadges = [];
            const followUps = [];
            if (this.effectiveCapabilities?.challenges && Alpine.store('session')?.rosterSelfPlayerId) {
                followUps.push(
                    apiFetch('/challenges?scope=mine&limit=4', { skipLogoutOn401: true })
                        .then(async (r) => (r.ok ? (await r.json().catch(() => ({}))) : null))
                        .then((j) => {
                            this.recentChallenges = Array.isArray(j?.items) ? j.items.slice(0, 4) : [];
                        })
                        .catch(() => {
                            this.recentChallenges = [];
                        }),
                );
            }
            if (this.effectiveCapabilities?.training) {
                followUps.push(
                    Promise.all([
                        apiFetch('/training/sessions/mine').then(async (r) => (r.ok ? (await r.json().catch(() => ({}))) : null)),
                        apiFetch('/training/progress/me').then(async (r) => (r.ok ? (await r.json().catch(() => ({}))) : null)),
                    ])
                        .then(([sessions, progress]) => {
                            this.trainingMine = Array.isArray(sessions?.items) ? sessions.items : [];
                            const baseSummary = this.summarizeTrainingSessions(this.trainingMine);
                            this.trainingProgress = progress || null;
                            this.trainingSummary = {
                                ...baseSummary,
                                completedTemplates: Number(progress?.summary?.completedTemplateCount || 0),
                                templateCount: Number(progress?.summary?.templateCount || 0),
                                badgeCounts: progress?.summary?.badgeCounts || { bronze: 0, silver: 0, gold: 0 },
                            };
                            this.recentTrainingBadges = Array.isArray(progress?.recentBadges)
                                ? progress.recentBadges.slice(0, 4)
                                : [];
                        })
                        .catch(() => {
                            this.trainingMine = [];
                            this.trainingSummary = this.summarizeTrainingSessions([]);
                            this.trainingProgress = null;
                            this.recentTrainingBadges = [];
                        }),
                );
            }
            if (followUps.length) {
                await Promise.allSettled(followUps);
            }
        } catch (e) {
            this.accountFixturesLeagues = [];
            this.accountPanelError = e?.message || 'Account panel load failed';
            this.accountControlPlane = null;
            this.accountSettingsBrief = null;
            this.backupSyncStatus = null;
            this.recentChallenges = [];
            this.trainingMine = [];
            this.trainingSummary = null;
            this.trainingProgress = null;
            this.recentTrainingBadges = [];
        } finally {
            this.accountFixturesLoading = false;
            this.playZoneLoading = false;
            await this.refreshVenueKioskQr();
            await this.loadPlatformSetupSnapshot();
        }
    },

    async loadAdminHomeLayout() {
        if (!this.isAdminUser()) return;
        try {
            const res = await apiFetch('/dashboard-layout/admin_home');
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data?.message || 'Failed to load admin dashboard layout');
            const widgets = Array.isArray(data?.widgets) ? data.widgets : [];
            try {
                this.adminHomeLayoutWidgets = JSON.parse(JSON.stringify(widgets));
            } catch {
                this.adminHomeLayoutWidgets = widgets.map((w) => ({ ...w, config: { ...(w?.config || {}) } }));
            }
            this.normalizeAdminHomeSections(widgets);
        } catch {
            this.adminHomeLayoutWidgets = [];
            this.normalizeAdminHomeSections([]);
        }
    },

    sortedStatsStrip() {
        return [...(this.statsStripWidgets || [])].sort(
            (a, b) => (a.order ?? 0) - (b.order ?? 0) || String(a.id).localeCompare(String(b.id)),
        );
    },

    dashboardTabStorageKey() {
        try {
            const id = Alpine.store('session')?.me?._id || Alpine.store('session')?.me?.id;
            return id ? `${ADMIN_DASHBOARD_TAB_LS_KEY}:${String(id)}` : `${ADMIN_DASHBOARD_TAB_LS_KEY}:anon`;
        } catch {
            return `${ADMIN_DASHBOARD_TAB_LS_KEY}:anon`;
        }
    },

    readDashboardTabFromQuery() {
        try {
            const tab = new URLSearchParams(globalThis.location?.search || '').get('tab');
            if (tab == null || tab === '') return false;
            const raw = String(tab).trim();
            if (raw === 'account') {
                this.dashboardActiveTab = 'account';
                this.writeDashboardTabToStorage();
                return true;
            }
            const leagueMatch = /^league:([a-z0-9_-]{1,24})$/i.exec(raw) || /^([a-z0-9_-]{1,24})$/i.exec(raw);
            if (leagueMatch) {
                const lk = leagueMatch[1].toUpperCase();
                this.dashboardActiveTab = `league:${lk}`;
                this.writeDashboardTabToStorage();
                this.persistActiveLeagueId(lk);
                return true;
            }
        } catch {
            // ignore
        }
        return false;
    },

    readDashboardTabFromStorage() {
        try {
            const raw = String(globalThis.localStorage?.getItem(this.dashboardTabStorageKey()) || '').trim();
            if (raw === 'account' || /^league:[A-Z0-9_-]{1,24}$/i.test(raw)) {
                this.dashboardActiveTab = raw === 'account'
                    ? 'account'
                    : `league:${raw.slice('league:'.length).toUpperCase()}`;
            }
        } catch {
            // ignore
        }
    },

    persistActiveLeagueId(leagueKey) {
        const clean = String(leagueKey || '').trim().toUpperCase();
        if (!/^[A-Z0-9_-]{1,24}$/.test(clean)) return;
        try {
            globalThis.localStorage?.setItem('dm.admin.league_id', clean);
        } catch {
            // ignore
        }
    },

    syncActiveLeagueFromDashboardTab() {
        if (!this.isLeagueDashboardTab()) return;
        this.persistActiveLeagueId(this.dashboardActiveTab.slice('league:'.length));
    },

    writeDashboardTabToStorage() {
        try {
            globalThis.localStorage?.setItem(this.dashboardTabStorageKey(), this.dashboardActiveTab);
        } catch {
            // ignore
        }
    },

    normalizeDashboardLeagueTabs() {
        const tabs = (Array.isArray(this.statsStripMetaLeagues) ? this.statsStripMetaLeagues : [])
            .map((opt) => {
                const leagueKey = String(opt?.leagueKey || opt?.key || '').trim().toUpperCase();
                const label =
                    String(opt?.label || opt?.name || leagueKey || '').trim() || leagueKey;
                return { leagueKey, label };
            })
            .filter((opt) => opt.leagueKey)
            .toSorted((a, b) => a.leagueKey.localeCompare(b.leagueKey));
        if (this.isLeagueDashboardTab()) {
            const lk = this.dashboardActiveTab.slice('league:'.length);
            if (lk && !tabs.some((t) => t.leagueKey === lk)) {
                tabs.push({ leagueKey: lk, label: lk });
                tabs.sort((a, b) => a.leagueKey.localeCompare(b.leagueKey));
            }
        }
        this.dashboardLeagueTabs = tabs;
        const valid = new Set(['account', ...tabs.map((t) => `league:${t.leagueKey}`)]);
        if (!valid.has(this.dashboardActiveTab)) {
            this.dashboardActiveTab = 'account';
            this.writeDashboardTabToStorage();
        }
    },

    dashboardTabsVisible() {
        return this.isAdminUser();
    },

    syncDashboardTabToUrl() {
        try {
            const url = new URL(globalThis.location.href);
            if (this.dashboardActiveTab === 'account') {
                url.searchParams.delete('tab');
            } else {
                url.searchParams.set('tab', this.dashboardActiveTab);
            }
            const next = `${url.pathname}${url.search}${url.hash}`;
            const now = `${globalThis.location.pathname}${globalThis.location.search}${globalThis.location.hash}`;
            if (now !== next) {
                globalThis.history.replaceState({}, '', next);
            }
        } catch {
            // ignore
        }
    },

    applyDashboardHomeTab() {
        if (!this.readDashboardTabFromQuery()) {
            this.dashboardActiveTab = 'account';
            this.writeDashboardTabToStorage();
            this.syncDashboardTabToUrl();
        }
    },

    isAccountDashboardTab() {
        return this.dashboardActiveTab === 'account';
    },

    isLeagueDashboardTab() {
        return !this.isAccountDashboardTab();
    },

    activeLeagueDashboardLabel() {
        if (this.isAccountDashboardTab()) return '';
        const key = this.dashboardActiveTab.slice('league:'.length);
        return this.dashboardLeagueTabs.find((t) => t.leagueKey === key)?.label || key;
    },

    leagueTabLabel(leagueKey = '') {
        const key = String(leagueKey || '').trim().toUpperCase();
        if (!key) return '';
        const tab = this.dashboardLeagueTabs.find((t) => t.leagueKey === key);
        if (tab?.label) return tab.label;
        const meta = (Array.isArray(this.statsStripMetaLeagues) ? this.statsStripMetaLeagues : []).find(
            (o) => String(o?.leagueKey || o?.key || '').trim().toUpperCase() === key,
        );
        return String(meta?.label || meta?.name || key).trim() || key;
    },

    enrichAccountFixturesLeagues(leagues = []) {
        return (Array.isArray(leagues) ? leagues : []).map((block) => {
            const leagueKey = String(block?.leagueKey || '').trim().toUpperCase();
            const label =
                String(block?.label || block?.leagueName || '').trim() || this.leagueTabLabel(leagueKey);
            return { ...block, leagueKey, label, leagueName: label };
        });
    },

    accountFixturesLeagueLabel(block) {
        const label = String(block?.label || block?.leagueName || '').trim();
        if (label) return label;
        return this.leagueTabLabel(block?.leagueKey) || block?.leagueKey || '';
    },

    async setDashboardTab(nextTab) {
        const next = String(nextTab || '').trim();
        if (!next || next === this.dashboardActiveTab) return;
        if (next !== 'account' && !/^league:[A-Z0-9_-]{1,24}$/i.test(next)) return;
        this.dashboardActiveTab = next === 'account'
            ? 'account'
            : `league:${next.slice('league:'.length).toUpperCase()}`;
        this.writeDashboardTabToStorage();
        this.syncDashboardTabToUrl();
        if (this.isLeagueDashboardTab()) {
            this.persistActiveLeagueId(this.dashboardActiveTab.slice('league:'.length));
            if (this.statsStripLayoutKey !== this.dashboardActiveTab) {
                this.setStatsStripLayout(this.dashboardActiveTab);
                return;
            }
            await this.loadStatsStrip();
        }
    },

    statsStripTabs() {
        const tabs = [{ key: 'default', label: 'Account' }];
        const opts = Array.isArray(this.statsStripMetaLeagues) ? this.statsStripMetaLeagues : [];
        for (const o of opts) {
            const k = String(o?.leagueKey || o?.key || '').trim().toUpperCase();
            if (k) tabs.push({ key: `league:${k}`, label: String(o?.label || o?.name || k) });
        }
        return tabs;
    },

    statsStripStorageKey() {
        try {
            const id = Alpine.store('session')?.me?._id || Alpine.store('session')?.me?.id;
            return id ? `${ADMIN_STATS_STRIP_LS_KEY}:${String(id)}` : `${ADMIN_STATS_STRIP_LS_KEY}:anon`;
        } catch {
            return `${ADMIN_STATS_STRIP_LS_KEY}:anon`;
        }
    },

    readStatsStripLayoutFromStorage() {
        try {
            const raw = globalThis.localStorage?.getItem(this.statsStripStorageKey());
            const s = String(raw || '').trim();
            if (!s) return;
            if (s === 'default' || s === 'combined' || /^league:[A-Z0-9_-]{1,24}$/i.test(s)) {
                this.statsStripLayoutKey = s.startsWith('league:')
                    ? `league:${s.slice('league:'.length).toUpperCase()}`
                    : s;
            }
        } catch {
            // ignore
        }
    },

    setStatsStripLayout(key) {
        const k = String(key || 'default').trim() || 'default';
        if (k === this.statsStripLayoutKey) return;
        this.statsStripLayoutKey = k;
        try {
            globalThis.localStorage?.setItem(this.statsStripStorageKey(), k);
        } catch {
            // ignore
        }
        this.loadStatsStrip();
    },

    async loadStatsStripMeta() {
        try {
            const res = await apiFetch('/dashboard-layout/meta');
            const j = await res.json().catch(() => ({}));
            if (!res.ok) return;
            this.statsStripMetaLeagues = Array.isArray(j.leagueOptions) ? j.leagueOptions : [];
        } catch {
            this.statsStripMetaLeagues = [];
        }
        this.normalizeDashboardLeagueTabs();
        const tabs = this.statsStripTabs();
        if (!tabs.some((t) => t.key === this.statsStripLayoutKey)) {
            this.statsStripLayoutKey = 'default';
            try {
                globalThis.localStorage?.setItem(this.statsStripStorageKey(), 'default');
            } catch {
                // ignore
            }
        }
    },

    async loadStatsStrip() {
        if (!this.isAdminUser()) {
            this.statsStripLoading = false;
            return;
        }
        this.statsStripLoading = true;
        this.statsStripError = '';
        try {
            const res = await apiFetch(
                `/dashboard-layout/admin-stats?layoutKey=${encodeURIComponent(this.statsStripLayoutKey)}&season=${encodeURIComponent(this.season)}`,
            );
            const j = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(j.message || res.statusText);
            this.statsStripLayoutScope = j.layoutScope === 'league' ? 'league' : 'account';
            this.statsStripFocusLeagueKey =
                j.focusLeagueKey != null && String(j.focusLeagueKey).trim()
                    ? String(j.focusLeagueKey).toUpperCase()
                    : null;
            this.statsStripWidgets = Array.isArray(j.widgets) ? j.widgets : [];
            this.statsStripSeasonUsed = String(j.seasonUsed || this.season || '');
        } catch (e) {
            this.statsStripError = e?.message || 'Failed to load admin overview';
            this.statsStripWidgets = [];
            this.statsStripLayoutScope = 'account';
            this.statsStripFocusLeagueKey = null;
        } finally {
            this.statsStripLoading = false;
        }
    },

    async init() {
        this.normalizeAdminHomeSections([]);
        this.applyDashboardHomeTab();
        this.syncActiveLeagueFromDashboardTab();
        this._leagueSnap = effectiveAdminLeagueId();
        await this.loadSeason(this.season);

        this.$watch(
            () => Alpine.store('session')?.me?._id,
            (id, prev) => {
                if (id && String(id) !== String(prev || '')) {
                    this.loadAdminHomeLayout();
                }
            },
        );

        this._onLeagueOrNav = () => {
            const onDashboard = String(globalThis.location.pathname || '').replace(/\/+$/, '').endsWith('/dashboard');
            if (onDashboard) {
                this.applyDashboardHomeTab();
            }
            if (adminLeagueDataSurface() !== 'dashboard') return;
            const next = effectiveAdminLeagueId();
            if (next === this._leagueSnap) return;
            this._leagueSnap = next;
            this.loadSeason(this.season);
        };
        document.addEventListener('pinecone:end', this._onLeagueOrNav);
        document.addEventListener('dm:admin-league-changed', this._onLeagueOrNav);

        this.$watch(() => Alpine.store('theme')?.value, (v) => { this.dark = v === 'dark'; this.updateChartsTheme(); });
        const classObserver = new MutationObserver(() => {
            const d = document.documentElement.classList.contains('dark');
            if (d !== this.dark) { this.dark = d; this.updateChartsTheme(); }
        });
        classObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
        this._observers.push(classObserver);

        const ro = new ResizeObserver(() => this.resizeCharts());
        ['#chart-team-points', '#chart-player-bestton', '#chart-team-bigs-stacked']
            .map(sel => document.querySelector(sel)?.parentElement)
            .filter(Boolean).forEach(el => ro.observe(el));
        this._observers.push(ro);

        this.$root.addEventListener('alpine:destroy', () => {
            if (this._onLeagueOrNav) {
                document.removeEventListener('pinecone:end', this._onLeagueOrNav);
                document.removeEventListener('dm:admin-league-changed', this._onLeagueOrNav);
                this._onLeagueOrNav = null;
            }
            this.teardown();
        });
    },

    async loadSeason(season = CURRENT_SEASON) {
        this.seasonLoading = true;
        this.season = String(season || CURRENT_SEASON);
        const league = encodeURIComponent(effectiveAdminLeagueId());
        try {
            const [tR, pR, mR, meR, sR] = await Promise.all([
                apiFetch(`/stats/teams?season=${encodeURIComponent(this.season)}&league=${league}`),
                apiFetch(`/stats/players?limit=20&season=${encodeURIComponent(this.season)}&league=${league}`),
                apiFetch(`/stats/me?season=${encodeURIComponent(this.season)}&league=${league}`),
                apiFetch('/auth/me'),
                apiFetch(`/stats/summary?season=${encodeURIComponent(this.season)}&league=${league}`),
            ]);

            const [t, p, m, meProfile, summary] = await Promise.all([
                tR.json(),
                pR.json(),
                mR.json(),
                meR.json(),
                sR.json(),
            ]);

            this.teams = t.items || [];
            this.playersTop = p.items || [];
            this.summary = summary || null;
            this.availableSeasons = summary?.availableSeasons?.length ? summary.availableSeasons : [this.season];
            const authPlayer = meProfile?.player && typeof meProfile.player === 'object' ? meProfile.player : null;
            const rawStats = m?.stats && typeof m.stats === 'object' ? m.stats : m;
            this.me = {
                player: authPlayer,
                stats: rawStats && typeof rawStats === 'object' ? rawStats : null,
            };

            const teamId = this.me?.player?.registeredTeamId || null;
            if (teamId) {
                await Promise.all([
                    this.fetchNextGame(teamId),
                    this.fetchLastGame(teamId),
                ]);
            } else {
                this.nextGame = null;
                this.lastGame = null;
            }

            this.renderCharts();
            this.updatedAtLabel = `Updated ${new Date().toLocaleString('en-GB')} - Season ${this.season}`;
            await this.loadAccountControlPanel();
            await this.loadAdminHomeLayout();
            await this.loadStatsStripMeta();
            if (this.isLeagueDashboardTab()) {
                this.statsStripLayoutKey = this.dashboardActiveTab;
                await this.loadStatsStrip();
            }
        } finally {
            this.seasonLoading = false;
        }
    },

    async changeSeason(value) {
        const nextSeason = String(value || CURRENT_SEASON);
        if (this.seasonLoading) return;
        await this.loadSeason(nextSeason);
    },

    renderCharts() {
        this.teardownCharts();

        this.charts.team = new ApexCharts(
            document.querySelector('#chart-team-points'),
            buildTeamPoints(this.teams, this.dark)
        );
        this.charts.playersBest = new ApexCharts(
            document.querySelector('#chart-player-bestton'),
            buildPlayerBestTon(this.playersTop, this.dark)
        );
        this.charts.teamsBigs = new ApexCharts(
            document.querySelector('#chart-team-bigs-stacked'),
            buildTeamsBigsStacked(this.teams, this.dark)
        );

        this.charts.team.render();
        this.charts.playersBest.render();
        this.charts.teamsBigs.render();
    },

    updateChartsTheme() {
        const c = this.charts;
        if (!c?.team || !c?.playersBest || !c?.teamsBigs) return;
        c.team.updateOptions(buildTeamPoints(this.teams, this.dark), false, true);
        c.playersBest.updateOptions(buildPlayerBestTon(this.playersTop, this.dark), false, true);
        c.teamsBigs.updateOptions(buildTeamsBigsStacked(this.teams, this.dark), false, true);
    },

    resizeCharts() {
        if (!this.charts || typeof this.charts !== 'object') return;
        Object.values(this.charts).forEach((ch) => ch?.updateOptions?.({}));
    },
    teardownCharts() {
        if (!this.charts || typeof this.charts !== 'object') return;
        Object.keys(this.charts).forEach((k) => {
            try {
                this.charts[k]?.destroy();
            } catch {
                /* ignore */
            }
            this.charts[k] = null;
        });
    },
    teardown() {
        this.teardownCharts();
        this._observers.forEach(o => { try { o.disconnect(); } catch { } });
        this._observers = [];
    },

    headerTitle() {
        if (!this.nextGame) return 'Your Next Game';
        const t = this.nextGame.type || 'fixture';
        if (t === 'event') return 'League Event';
        if (t === 'break') return 'League Break';
        return 'Your Next Game';
    },

    venueNameFor(fx) {
        return fx?.venue?.name ? `at ${fx.venue.name}` : null;
    },

    buildSheetBreakdown(sheet) {
        return {
            singles: `${sheet?.totals?.singles?.home || 0}-${sheet?.totals?.singles?.away || 0}`,
            doubles: `${sheet?.totals?.doubles?.home || 0}-${sheet?.totals?.doubles?.away || 0}`,
            beer: `${sheet?.totals?.beer?.[0] || 0}-${sheet?.totals?.beer?.[1] || 0}`,
            match: `${sheet?.totals?.match?.[0] || 0}-${sheet?.totals?.match?.[1] || 0}`,
        };
    },

    tonsLeaderLabel() {
        return this.summary?.tonsLeader?.playerName || 'No leader yet';
    },
    tonsLeaderTeam() {
        return this.summary?.tonsLeader?.teamName || 'Season not started';
    },
    tonsLeaderValue() {
        return this.summary?.tonsLeader?.value || 0;
    },
    tonsLeaderHref() {
        const id = this.summary?.tonsLeader?.playerId;
        return id ? `/admin/players/${id}` : null;
    },
    highestCheckoutLabel() {
        return this.summary?.highestCheckoutLeader?.playerName || 'No checkout yet';
    },
    highestCheckoutTeam() {
        return this.summary?.highestCheckoutLeader?.teamName || 'Season not started';
    },
    highestCheckoutValue() {
        return this.summary?.highestCheckoutLeader?.value || 0;
    },
    highestCheckoutHref() {
        const id = this.summary?.highestCheckoutLeader?.playerId;
        return id ? `/admin/players/${id}` : null;
    },
    leadingTeam() {
        return this.teams?.[0] || null;
    },
    leadingTeamHref() {
        const id = this.leadingTeam()?.teamId;
        return id ? `/admin/teams/${id}` : null;
    },
    leadingTeamName() {
        return this.leadingTeam()?.teamName || 'No leader yet';
    },
    leadingTeamLogo() {
        return this.leadingTeam()?.teamLogo || '/assets/placeholder-team.png';
    },
    leadingTeamPoints() {
        return this.leadingTeam()?.points || 0;
    },
    leadingTeamMargin() {
        if (!this.teams?.length) return 0;
        const top = Number(this.teams[0]?.points || 0);
        const next = Number(this.teams[1]?.points || 0);
        return Math.max(0, top - next);
    },
    leadingTeamMarginLabel() {
        if (!this.teams?.length) return 'Season not started';
        if (this.teams.length === 1) return 'Only team on the board';
        const margin = this.leadingTeamMargin();
        if (!margin) return 'Level on points';
        return `Leading by ${margin} point${margin === 1 ? '' : 's'}`;
    },
    teamHref(team) {
        const id = team?.teamId || team?._id;
        return id ? `/admin/teams/${id}` : null;
    },
    playerHref(player) {
        const id = player?.playerId || player?._id;
        return id ? `/admin/players/${id}` : null;
    },

    async fetchNextGame(teamId) {
        this.nextLoading = true;
        this.nextError = '';
        try {
            const res = await apiFetch(`/fixtures/next?teamId=${encodeURIComponent(teamId)}`);
            if (!res.ok) {
                return;
            }

            const data = await res.json();
            this.nextGame = unwrapApiEntity(data, ['fixture', 'item']) || null;

            if (this.nextGame) {
                const type = this.nextGame.type || 'fixture';
                const { short, long, raw } = fmtDateTime(this.nextGame.startAt);

                const common = {
                    type,
                    whenShort: short,
                    whenLong: long,
                    whenRaw: raw,
                    until: timeUntil(raw),
                    href: withLeagueId(`/admin/fixtures/${this.nextGame._id}`, effectiveAdminLeagueId()),
                };

                if (type === 'fixture') {
                    const homeId =
                        this.nextGame.homeTeamId?._id ||
                        this.nextGame.homeTeam?._id ||
                        this.nextGame.homeTeamId || null;

                    const isHome = String(homeId) === String(teamId);
                    const homeName = this.nextGame.homeTeamName || this.nextGame.homeTeam?.name || 'Home Team';
                    const awayName = this.nextGame.awayTeamName || this.nextGame.awayTeam?.name || 'Away Team';

                    this.nextGame._ui = {
                        ...common,
                        homeName,
                        awayName,
                        isHome,
                        myTeam: isHome ? homeName : awayName,
                        oppTeam: isHome ? awayName : homeName,
                        venueName: this.venueNameFor(this.nextGame) || '-',
                        venueHref: this.nextGame.venue ? `/admin/venues/${this.nextGame.venue?._id}` : null,
                        mapsUrl: this.nextGame.mapsUrl || this.nextGame.venue?.mapsUrl || null,
                        logoHome: this.nextGame.homeTeamId?.logoUrl || null,
                        logoAway: this.nextGame.awayTeamId?.logoUrl || null,
                    };
                } else if (type === 'event') {
                    this.nextGame._ui = {
                        ...common,
                        eventTitle: this.nextGame?.title || '',
                        venueName: this.nextGame.venue ? this.nextGame.venue.name : '',
                        venueHref: this.nextGame.venue ? `/admin/venues/${this.nextGame.venue?._id}` : null,
                        venueImage: this.nextGame.venue ? this.nextGame.venue.imageUrl : null,
                        mapsUrl: this.nextGame.venue ? this.nextGame.venue?.mapsUrl : null,
                    };
                } else if (type === 'break') {
                    this.nextGame._ui = { ...common };
                }
            }
        } catch (e) {
            this.nextError = e?.message || 'Unable to load next game';
            this.nextGame = null;
        } finally {
            this.nextLoading = false;
        }
    },

    async fetchLastGame(teamId) {
        this.lastLoading = true;
        this.lastError = '';
        try {
            const res = await apiFetch(`/fixtures/last?teamId=${encodeURIComponent(teamId)}`);
            if (!res.ok) {
                this.lastGame = null;
                return;
            }

            const data = await res.json();
            this.lastGame = unwrapApiEntity(data, ['fixture', 'item']);

            if (!this.lastGame) return;

            const homeId =
                this.lastGame.homeTeamId?._id ||
                this.lastGame.homeTeam?._id ||
                this.lastGame.homeTeamId || null;

            const isHome = String(homeId) === String(teamId);
            const homeName = this.lastGame.homeTeamName || this.lastGame.homeTeamId?.name || 'Home Team';
            const awayName = this.lastGame.awayTeamName || this.lastGame.awayTeamId?.name || 'Away Team';
            const { short, long } = fmtDateTime(this.lastGame.startAt);

            this.lastGame._ui = {
                whenShort: short,
                whenLong: long,
                whenDate: new Date(this.lastGame.startAt).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }),
                homeName,
                awayName,
                isHome,
                myTeam: isHome ? homeName : awayName,
                oppTeam: isHome ? awayName : homeName,
                href: withLeagueId(`/admin/fixtures/${this.lastGame._id}`, effectiveAdminLeagueId()),
                result: (() => {
                    const h = this.lastGame.legsHome ?? 0;
                    const a = this.lastGame.legsAway ?? 0;
                    return this.lastGame.forfeit ? `${h}–${a} (forfeit)` : `${h} - ${a}`;
                })(),
                logoHome: this.lastGame.homeTeamId?.logoUrl || null,
                logoAway: this.lastGame.awayTeamId?.logoUrl || null,
                scoresheetHref: null,
                breakdown: null,
            };

            const sheetRes = await apiFetch(`/scoresheets/by-fixture/${encodeURIComponent(this.lastGame._id)}`);
            if (!sheetRes.ok) return;
            const sheetData = await sheetRes.json();
            const sheet = sheetData?.sheet || null;
            if (!sheet?._id) return;

            this.lastGame._ui.scoresheetHref = `/admin/scoresheet/${sheet._id}`;
            if (sheet.status === 'approved') {
                this.lastGame._ui.breakdown = this.buildSheetBreakdown(sheet);
            }
        } catch (e) {
            this.lastError = e?.message || 'Unable to load last result';
            this.lastGame = null;
        } finally {
            this.lastLoading = false;
        }
    }
}));
